<template>
  <div class="chart-data-page-content common-title-page">
    <div class="title-wrapper">
      <pods-input ref="podsInput"></pods-input>
      <span class="title">{{ $t('text.chart_pod_capacitance_readings') }}</span>
      <DatePicker />
      <el-button
        v-if="p('Pods.Capacitance Readings Chart.Export')"
        class="save-btn min-w-80 mini-btn capacitance-reading-export-btn"
        @click="exportData()"
        :loading="downloadLoading"
        >{{ $t('text.export') }}</el-button
      >
    </div>
    <div class="page-content" v-loading="loading">
      <div class="dark-font bold-font">
        {{ $t('text.recent_data_for') }}
      </div>
      <div class="chart-wrapper">
        <div
          id="echarts"
          class="mt-10"
          :style="{
            width: '100%',
            height: '400px'
          }"
        ></div>
        <TagBox
          ref="tagBox"
          :fromDate="fromDate"
          :toDate="toDate"
          :podMac="this.$store.state.pods.selectedPod.podId"
          :utcOffset="this.$store.state.pods.selectedUtcOffset"
          @savedSuccess="handleAfterSavedTag()"
          v-if="showTagBox"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PodsInput from '@/components/PodsInput'
import DatePicker from '@/components/DatePicker'
import TagBox from '@/components/TagBox'
import swal from 'sweetalert'
import ApiService from '@/services/ApiService.js'
import moment from 'moment-timezone'
import { converFile } from '../../../utils/utils'
export default {
  data() {
    return {
      showTagBox: false,
      chart: '',
      fromDate: '',
      toDate: '',
      downloadLoading: false
    }
  },
  props: {
    loading: {
      type: Boolean,
      required: true
    }
  },
  components: {
    PodsInput,
    DatePicker,
    TagBox
  },
  methods: {
    firstLetterCapitalize(val) {
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    initChart() {
      let source = [['timeStampWithUtc', 'sensor1Value', 'sensor2Value']]
      this.$parent.$parent.$parent.source.forEach((item, index) => {
        if (index > 0) {
          source.push([item[0], item[1], item[2]])
        }
      })
      let vm = this
      var dom = document.getElementById('echarts')
      this.chart = this.echarts.init(dom)
      this.chart.setOption({
        grid: {
          left: 0,
          right: 20,
          bottom: 50,
          top: 30,
          containLabel: true
        },
        toolbox: {
          show: source[1] && source[1][0],
          feature: {
            brush: {
              title: {
                lineX: this.$t('text.select'),
                clear: this.$t('text.clear_selected')
              }
            }
          }
        },
        tooltip: this.$parent.$parent.$parent.tooltip,
        color: ['#008ffb', '#00e396'],
        legend: {
          x: 'center',
          y: 'bottom',
          bottom: 0,
          data: [this.$t('text.sensor') + ' 1', this.$t('text.sensor') + ' 2']
        },
        dataZoom: [
          {
            show: true,
            height: 25,
            bottom: 25,
            start: 0,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true
          },
          {
            type: 'inside'
          }
        ],
        dataset: {
          dimensions: ['timeStampWithUtc', 'sensor1Value', 'sensor2Value'],
          source: source
        },
        xAxis: [
          {
            type: 'time',
            boundaryGap: false,
            splitLine: {
              show: false
            },
            axisLabel: {
              show: true,
              showMinLabel: true,
              showMaxLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            nameLocation: 'center',
            nameGap: 40,
            axisLine: {
              show: false
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: ['#f2f2f2'],
                width: 1,
                type: 'solid'
              }
            }
          }
        ],
        series: [
          {
            name: this.$t('text.sensor') + ' 1',
            type: 'line',
            symbol: 'circle',
            smooth: true,
            encode: {
              x: 'timeStampWithUtc',
              y: 'sensor1Value'
            }
          },
          {
            name: this.$t('text.sensor') + ' 2',
            type: 'line',
            symbol: 'circle',
            smooth: true,
            encode: {
              x: 'timeStampWithUtc',
              y: 'sensor2Value'
            }
          }
        ],
        brush: {
          toolbox: ['lineX', 'clear'],
          xAxisIndex: 0,
          throttleType: 'debounce',
          throttleDelay: 200,
          brushStyle: {
            borderWidth: 2,
            color: 'rgba(255, 255, 0, 0.15)',
            borderColor: '#ec808d'
          }
        }
      })
      this.chart.off('brushSelected')
      this.chart.on('brushSelected', this.brushSelected)
      this.chart.on('brushEnd', this.brushEnd)
      window.addEventListener('resize', function() {
        vm.chart.resize()
      })
    },
    brushSelected(params) {
      if (this.showTagBox && !params.batch[0].areas.length) {
        this.clearTagForm()
      }
    },
    clearTagForm() {
      this.showTagBox = false
      this.$refs.tagBox.$refs.tagForm.resetFields()
      this.$refs.tagBox.tagDataForm = {
        tag: [],
        notes: ''
      }
      this.fromDate = this.toDate = ''
    },
    brushEnd(params) {
      this.showTagBox = true

      //Gets the index of the selected region
      let tagSelectedArea = params.areas[0].range
      let seleted = this.chart._chartsViews[0]._points
        .map(([left], i) => {
          return left >= tagSelectedArea[0] && left <= tagSelectedArea[1]
            ? i
            : -1
        })
        .filter(item => {
          return item >= 0
        })

      //Gets the data for the visual region after scaling
      let shownSource = []
      let rangeStart = this.chart.getModel().option.xAxis[0].rangeStart
      let rangeEnd = this.chart.getModel().option.xAxis[0].rangeEnd
      this.$parent.$parent.$parent.source.forEach((item, index) => {
        if (index > 0) {
          if (rangeStart && rangeEnd) {
            if (
              moment(item[0]).valueOf() >= rangeStart &&
              moment(item[0]).valueOf() <= rangeEnd
            ) {
              shownSource.push(item)
            }
          } else {
            shownSource.push(item)
          }
        }
      })

      this.fromDate = shownSource[seleted[0]][9]
      this.toDate = shownSource[seleted[seleted.length - 1]][9]
    },
    handleAfterSavedTag() {
      this.clearTagForm()
      this.chart.dispatchAction({
        type: 'brush',
        areas: []
      })
    },
    exportData() {
      this.downloadLoading = true
      ApiService.DownloadRawData(
        this.$store.state.pods.selectedPod.podId,
        moment(this.$store.state.pods.selectedDate[0]).format(
          'YYYY-MM-DD HH:mm:ss'
        ),
        moment(this.$store.state.pods.selectedDate[1]).format(
          'YYYY-MM-DD HH:mm:ss'
        ),
        moment.tz(this.$store.state.pods.selectedUtcOffset).utcOffset() / 60
      )
        .then(response => {
          this.downloadLoading = false
          if (!response.data) return false
          let reportName =
            this.$store.state.pods.selectedPod.podId +
            ' ' +
            moment(this.$store.state.pods.selectedDate[0]).format(
              'YYYY-MM-DD HH:mm:ss'
            ) +
            ' to ' +
            moment(this.$store.state.pods.selectedDate[1]).format(
              'YYYY-MM-DD HH:mm:ss'
            ) +
            'Pod Data.csv'
          converFile(reportName, response.data)
        })
        .catch(() => {
          this.downloadLoading = false
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed')
          })
        })
    }
  },
  beforeDestroy() {
    this.chart.clear()
  }
}
</script>
